import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../lib/api'
import { usePracticeSession } from '../hooks/usePracticeSession'
import { ScenarioTraineeView } from '../components/scenario/ScenarioPlayer'
import type { Scenario } from '../components/scenario/ScenarioPlayer'

export default function PracticeSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { isSignedIn } = useAuth()
  const navigate = useNavigate()
  const [joined, setJoined] = useState(false)
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [hasResponded, setHasResponded] = useState(false)
  const [currentScenarioId, setCurrentScenarioId] = useState<string | null>(null)

  const { state, error: sseError, connected } = usePracticeSession(sessionId ?? null)

  useEffect(() => {
    if (!isSignedIn) return
    if (!sessionId || joined) return

    let cancelled = false

    async function join() {
      setJoining(true)
      setJoinError(null)
      try {
        await api.joinPracticeSession(sessionId!)
        if (!cancelled) setJoined(true)
      } catch (err) {
        if (!cancelled) {
          setJoinError(err instanceof Error ? err.message : 'Failed to join session')
        }
      } finally {
        if (!cancelled) setJoining(false)
      }
    }

    join()
    return () => { cancelled = true }
  }, [isSignedIn, sessionId, joined])

  // Reset hasResponded when scenario changes
  useEffect(() => {
    if (state?.scenarioId && state.scenarioId !== currentScenarioId) {
      setCurrentScenarioId(state.scenarioId)
      setHasResponded(false)
      setSubmitError(null)
    }
  }, [state?.scenarioId, currentScenarioId])

  const handleSubmit = async (responseText?: string) => {
    if (!sessionId || !state?.scenarioId) return
    setSubmitError(null)
    try {
      await api.submitPracticeResponse(sessionId, {
        scenario_id: state.scenarioId,
        response_text: responseText,
      })
      setHasResponded(true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit'
      setSubmitError(msg)
      throw err
    }
  }

  if (!isSignedIn) {
    return (
      <div className="practice-session-page">
        <div className="practice-session-card">
          <p>Please sign in to join this practice session.</p>
        </div>
      </div>
    )
  }

  if (joining) {
    return (
      <div className="practice-session-page">
        <div className="practice-session-card">
          <p>Joining session…</p>
        </div>
      </div>
    )
  }

  if (joinError) {
    return (
      <div className="practice-session-page">
        <div className="practice-session-card">
          <p className="error">{joinError}</p>
          <button onClick={() => navigate('/learn')} className="btn">
            Back to Learning
          </button>
        </div>
      </div>
    )
  }

  if (state?.status === 'ended') {
    return (
      <div className="practice-session-page">
        <div className="practice-session-card">
          <h2>Session Ended</h2>
          <p>The facilitator has ended this practice session.</p>
          <button onClick={() => navigate('/learn')} className="btn btn-primary">
            Back to Learning
          </button>
        </div>
      </div>
    )
  }

  const scenario: Scenario | null = state?.scenarioData
    ? (state.scenarioData as Scenario)
    : null

  return (
    <div className="practice-session-page">
      <div className="practice-session-header">
        <div className="practice-session-meta">
          <span className="practice-session-id">Session {sessionId?.slice(0, 8)}</span>
          <span className={`practice-session-status ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        </div>
      </div>

      {sseError && (
        <div className="practice-session-alert">
          {sseError}
        </div>
      )}

      {submitError && (
        <div className="practice-session-alert error">
          {submitError}
        </div>
      )}

      {scenario ? (
        <ScenarioTraineeView
          sessionId={sessionId!}
          scenario={scenario}
          onSubmit={handleSubmit}
          hasResponded={hasResponded}
        />
      ) : (
        <div className="practice-session-card">
          <div className="scenario-waiting">
            <h2>Waiting for facilitator…</h2>
            <p>The facilitator will push the first scenario shortly.</p>
          </div>
        </div>
      )}
    </div>
  )
}
